import {
  ChatPart,
  MetricCardSuggestionItem,
  MealItemDetailsPayload,
  MockScenarioId,
  MockScenarioResponse,
  SuggestionItem,
  WorkoutDetailsPayload,
} from "../types";
import { MOCK_IMAGE_ASSETS } from "./mockAssets";

type ScenarioDefinition = {
  id: MockScenarioId;
  commandSuffix: string;
  title: string;
  description: string;
  response: Omit<MockScenarioResponse, "scenarioId">;
};

const METRIC_SELECTION_PREFIX = "select metric:";

const withImmediateSend = (items: Array<Omit<SuggestionItem, "sendMode">>) => {
  return items.map((item) => ({
    ...item,
    sendMode: "immediate" as const,
  }));
};

const SCENARIOS: ScenarioDefinition[] = [
  {
    id: "plain_text",
    commandSuffix: "plain",
    title: "Plain Text",
    description: "Single assistant text bubble",
    response: {
      parts: [
        {
          type: "text",
          text: "This is a plain text response. Use `image`, `link`, or `metric` to test other rendering states.",
        },
      ],
    },
  },
  {
    id: "image_text",
    commandSuffix: "image",
    title: "Image + Text",
    description: "Image block and explanatory text",
    response: {
      parts: [
        {
          type: "image",
          src: MOCK_IMAGE_ASSETS.respiratoryDashboard,
          alt: "Respiratory dashboard snapshot",
          caption: "Sample respiratory dashboard used for mock rendering.",
        },
        {
          type: "text",
          text: "This scenario validates image rendering in an assistant turn with supporting narrative text.",
        },
      ],
    },
  },
  {
    id: "suggestions",
    commandSuffix: "suggestions",
    title: "Suggestions",
    description: "Selectable 2–3 option prompt chips",
    response: {
      parts: [
        {
          type: "text",
          text: "Pick one of these suggested prompts. Each option sends immediately.",
        },
        {
          type: "suggestions",
          items: withImmediateSend([
            {
              id: "inline-suggestion-1",
              label: "Summarize my report",
              prompt: "Summarize my latest report in three bullets",
            },
            {
              id: "inline-suggestion-2",
              label: "Next best action",
              prompt: "What should I do in the next 24 hours?",
            },
            {
              id: "inline-suggestion-3",
              label: "Plan for tomorrow",
              prompt: "Build a simple plan for tomorrow",
            },
          ]),
        },
      ],
    },
  },
  {
    id: "custom_metric",
    commandSuffix: "metric",
    title: "Custom Metric Card",
    description: "Custom UI card part rendering",
    response: {
      parts: [
        {
          type: "text",
          text: "This custom metric card is rendered from structured data.",
        },
        {
          type: "custom_metric_card",
          data: {
            metricId: "ventilatory_efficiency",
            title: "Ventilatory Efficiency",
            score: 78,
            status: "watch",
            trend: "up",
            statusLabel: "Watch",
            trendLabel: "Up",
            summary:
              "Efficiency has improved versus last test, but remains below your target range.",
            deltaLabel: "+6 points vs. last test",
          },
        },
      ],
    },
  },
  {
    id: "metric_picker",
    commandSuffix: "metric-picker",
    title: "Metric Picker",
    description: "Three selectable metric cards that send immediately",
    response: {
      parts: [
        {
          type: "text",
          text: "Choose one metric card below. Clicking a card sends your selection immediately.",
        },
        {
          type: "metric_card_suggestions",
          data: {
            items: buildMetricCardSuggestions(),
            ui: {
              selectedBadgeLabel: "Selected",
            },
          },
        },
      ],
    },
  },
  {
    id: "custom_graph",
    commandSuffix: "graph",
    title: "Custom Graph",
    description: "Platform-style metric trend line chart",
    response: {
      parts: [
        {
          type: "text",
          text: "This graph is based on the platform metric history pattern and shows a 7-point trend for a score metric.",
        },
        {
          type: "custom_trend_chart",
          data: {
            chartId: "breath_score_trend",
            title: "Breath Score Trend",
            metricLabel: "Breath Score",
            unit: "%",
            trend: "up",
            trendLabel: "Up",
            latestValueLabel: "81%",
            summary:
              "Trend is improving over the last seven checkpoints and is now above the target threshold.",
            target: 75,
            ui: {
              targetLabel: "Target",
            },
            points: [
              { label: "Mon", value: 56 },
              { label: "Tue", value: 59 },
              { label: "Wed", value: 62 },
              { label: "Thu", value: 68 },
              { label: "Fri", value: 71 },
              { label: "Sat", value: 76 },
              { label: "Sun", value: 81 },
            ],
          },
        },
      ],
    },
  },
  {
    id: "meal_item_details",
    commandSuffix: "meal",
    title: "Meal Item Details",
    description: "Recipe-style custom response card",
    response: {
      parts: [
        {
          type: "text",
          text: "This custom meal card mirrors the Healthub recipe detail hierarchy using a web-specific component.",
        },
        {
          type: "custom_meal_item_details",
          data: buildMockMealItemDetails(),
        },
      ],
    },
  },
  {
    id: "workout_details",
    commandSuffix: "workout",
    title: "Workout Details",
    description: "Workout detail block with wearable and exercise data",
    response: {
      parts: [
        {
          type: "text",
          text: "This custom workout card mirrors the Healthub workout detail flow using normalized mock data.",
        },
        {
          type: "custom_workout_details",
          data: buildMockWorkoutDetails(),
        },
      ],
    },
  },
  {
    id: "error_state",
    commandSuffix: "error",
    title: "Error State",
    description: "Simulated backend failure with retry",
    response: {
      parts: [],
      error: {
        summaryText: "Mock runtime error: simulated upstream failure.",
        retryLabel: "Retry",
      },
    },
  },
];

const SCENARIO_BY_ID = SCENARIOS.reduce<Record<MockScenarioId, ScenarioDefinition>>(
  (acc, scenario) => {
    acc[scenario.id] = scenario;
    return acc;
  },
  {} as Record<MockScenarioId, ScenarioDefinition>
);

const SCENARIO_BY_COMMAND = SCENARIOS.reduce<Record<string, MockScenarioId>>(
  (acc, scenario) => {
    acc[scenario.commandSuffix] = scenario.id;
    return acc;
  },
  {}
);

export const DEFAULT_WELCOME_SUGGESTIONS: SuggestionItem[] = withImmediateSend([
  {
    id: "welcome-plain",
    label: "Plain text",
    prompt: "plain",
  },
  {
    id: "welcome-link",
    label: "Link card",
    prompt: "link",
  },
  {
    id: "welcome-meal",
    label: "Meal card",
    prompt: "meal",
  },
  {
    id: "welcome-workout",
    label: "Workout card",
    prompt: "workout",
  },
]);

export const MOCK_SCENARIO_OPTIONS = SCENARIOS.map((scenario) => ({
  id: scenario.id,
  title: scenario.title,
  description: scenario.description,
}));

export const getScenarioCommand = (scenarioId: MockScenarioId): string => {
  return SCENARIO_BY_ID[scenarioId].commandSuffix;
};

export type ParsedDemoScenarioInput = {
  scenarioId: MockScenarioId;
  plainTextInput?: string;
};

export const parseDemoScenarioCommandFromInput = (
  input: string
): ParsedDemoScenarioInput | null => {
  const normalizedInput = input.trim();
  if (!normalizedInput) {
    return null;
  }

  const demoCommandMatch = normalizedInput.match(/^([^\s]+)(?:\s+([\s\S]+))?$/);
  if (!demoCommandMatch) {
    return null;
  }

  const commandSuffix = demoCommandMatch[1].toLowerCase();
  if (commandSuffix.startsWith("/")) {
    return null;
  }
  const scenarioId = SCENARIO_BY_COMMAND[commandSuffix];
  if (!scenarioId) {
    return null;
  }

  const commandPayload = demoCommandMatch[2]?.trim();
  if (scenarioId !== "plain_text" || !commandPayload) {
    return { scenarioId };
  }

  return {
    scenarioId,
    plainTextInput: commandPayload,
  };
};

export const parseDemoScenarioFromInput = (input: string): MockScenarioId | null => {
  const parsedScenario = parseDemoScenarioCommandFromInput(input);
  return parsedScenario ? parsedScenario.scenarioId : null;
};

export const getScenarioResponse = (
  scenarioId: MockScenarioId,
  overrides?: {
    plainTextInput?: string;
  }
): MockScenarioResponse => {
  const scenario = SCENARIO_BY_ID[scenarioId];
  const baseResponse: MockScenarioResponse = {
    scenarioId: scenario.id,
    ...scenario.response,
  };

  if (scenarioId !== "plain_text") {
    return baseResponse;
  }

  const plainTextInput = overrides?.plainTextInput?.trim();
  if (!plainTextInput) {
    return baseResponse;
  }

  return {
    ...baseResponse,
    parts: [
      {
        type: "text",
        text: plainTextInput,
      },
    ],
  };
};

export const buildFallbackResponse = (userInput: string): MockScenarioResponse => {
  const normalizedInput = userInput.trim();
  const metricSelectionResponse = buildMetricSelectionResponse(normalizedInput);
  if (metricSelectionResponse) {
    return metricSelectionResponse;
  }

  const promptEcho = normalizedInput || "(empty input)";

  const parts: ChatPart[] = [
    {
      type: "text",
      text:
        "Mock response: this is deterministic local behavior before wiring any model provider.",
    },
    {
      type: "text",
      text: `Input received: ${promptEcho}`,
    },
  ];

  return {
    scenarioId: "plain_text",
    parts,
  };
};

function buildMetricCardSuggestions(): MetricCardSuggestionItem[] {
  return [
    {
      id: "metric-choice-ventilatory-efficiency",
      metricId: "ventilatory_efficiency",
      title: "Ventilatory Efficiency",
      score: 78,
      status: "watch",
      trend: "up",
      statusLabel: "Watch",
      trendLabel: "Up",
      summary:
        "Efficiency is improving, but still sits below the preferred range for your profile.",
      deltaLabel: "+6 points vs. last test",
      prompt: "Select metric: Ventilatory Efficiency",
      sendMode: "immediate",
    },
    {
      id: "metric-choice-fat-oxidation",
      metricId: "fat_oxidation_capacity",
      title: "Fat Oxidation Capacity",
      score: 84,
      status: "good",
      trend: "up",
      statusLabel: "Good",
      trendLabel: "Up",
      summary:
        "Your fat utilization profile is trending in the right direction during lower-intensity work.",
      deltaLabel: "+4 points vs. baseline",
      prompt: "Select metric: Fat Oxidation Capacity",
      sendMode: "immediate",
    },
    {
      id: "metric-choice-carb-flexibility",
      metricId: "carbohydrate_flexibility",
      title: "Carbohydrate Flexibility",
      score: 61,
      status: "risk",
      trend: "down",
      statusLabel: "Risk",
      trendLabel: "Down",
      summary:
        "High dependence on carbohydrate remains visible and may limit long-duration efficiency.",
      deltaLabel: "-5 points this month",
      prompt: "Select metric: Carbohydrate Flexibility",
      sendMode: "immediate",
    },
  ];
}

const buildMetricSelectionResponse = (
  userInput: string
): MockScenarioResponse | null => {
  const normalizedInput = userInput.trim();
  if (
    normalizedInput.toLowerCase().startsWith(METRIC_SELECTION_PREFIX) === false
  ) {
    return null;
  }

  const selectedMetric = normalizedInput
    .slice(METRIC_SELECTION_PREFIX.length)
    .trim();

  if (!selectedMetric) {
    return null;
  }

  return {
    scenarioId: "metric_picker",
    parts: [
      {
        type: "text",
        text: `You selected ${selectedMetric}.`,
      },
      {
        type: "text",
        text: "This is the mock acknowledgment path for metric card selection.",
      },
    ],
  };
};

function buildMockMealItemDetails(): MealItemDetailsPayload {
  return {
    id: "meal-recipe-1",
    kind: "recipe",
    title: "Mediterranean Salmon Bowl",
    subtitle: "High Protein, Mediterranean, Gluten Free",
    imageUrl: MOCK_IMAGE_ASSETS.mealCard,
    statusTags: ["logged", "nutritionist_approved"],
    score: {
      value: 86,
      label: "Strong",
      tone: "good",
    },
    metadata: [
      { label: "Preparation", value: "20 min" },
      { label: "Meal score", value: "86% Strong" },
      { label: "Difficulty", value: "Medium" },
    ],
    nutritionFacts: {
      calories: 540,
      proteinGrams: 38,
      carbsGrams: 42,
      fatGrams: 24,
      micronutrients: [
        { label: "Omega-3", value: 2.1, unit: "g" },
        { label: "Vitamin D", value: 9, unit: "mcg" },
        { label: "Magnesium", value: 110, unit: "mg" },
      ],
    },
    ingredients: [
      { id: "i1", name: "Salmon fillet", quantity: "180 g" },
      { id: "i2", name: "Quinoa", quantity: "120 g" },
      { id: "i3", name: "Cucumber", quantity: "80 g" },
      { id: "i4", name: "Cherry tomatoes", quantity: "70 g" },
      { id: "i5", name: "Olive oil", quantity: "1 tbsp" },
    ],
    instructions: [
      "Cook the quinoa until fluffy.",
      "Pan-sear the salmon for 4 to 5 minutes per side.",
      "Assemble the bowl with vegetables and quinoa.",
      "Finish with olive oil and lemon.",
    ],
    additionalInformation: [
      { label: "Consume within", value: "2 days" },
      { label: "Key ingredient", value: "Salmon" },
    ],
    groceryList: [
      { id: "g1", name: "Salmon fillet", quantity: "1 pack" },
      { id: "g2", name: "Quinoa", quantity: "1 bag" },
      { id: "g3", name: "Cherry tomatoes", quantity: "1 box" },
    ],
    sourceContext: {
      date: "2026-02-28",
      mealType: 3,
      isAlternative: false,
      isExtra: false,
      isLogged: true,
    },
    ui: {
      kindLabels: {
        recipe: "Recipe",
        food: "Food item",
      },
      statusLabels: {
        logged: "Logged",
        skipped: "Skipped",
        swapped: "Swapped",
        extra: "Extra",
        personalized: "Personalized",
        nutritionist_approved: "Approved",
      },
      sectionTitles: {
        overview: "Overview",
        nutritionFacts: "Nutrition Facts",
        micronutrientsToggle: "View micronutrients",
        ingredients: "Ingredients",
        servingDetails: "Serving details",
        instructions: "Instructions",
        additionalInformation: "Additional Information",
        groceryList: "Grocery List",
      },
      nutritionFactLabels: {
        calories: "Calories",
        protein: "Protein",
        carbs: "Carbs",
        fat: "Fat",
      },
    },
  };
}

function buildMockWorkoutDetails(): WorkoutDetailsPayload {
  return {
    id: "workout-1",
    title: "Lower Body Strength",
    dayLabel: "Sat 28 Feb",
    activityType: "Resistance",
    isScheduled: true,
    stateTag: "completed",
    calories: {
      current: 420,
      target: 500,
    },
    summaryMetrics: [
      { label: "Duration", value: "45 min" },
      { label: "Type", value: "Resistance" },
      { label: "State", value: "Completed" },
    ],
    wearableData: [
      {
        id: "we1",
        title: "Apple Watch Session",
        calories: 420,
        durationMinutes: 44,
        startedAt: "2026-02-28T10:15:00Z",
        startedAtLabel: "Feb 28, 10:15 AM",
        hasHeartRateData: true,
        inputSource: "wearable",
        sourceLabel: "wearable",
      },
    ],
    exerciseGroups: [
      {
        id: "group-1",
        typeLabel: "Superset",
        description: "Alternate both movements for 3 rounds.",
        totalRounds: 3,
        exercises: [
          {
            id: "ex-1",
            title: "Goblet Squat",
            imageUrl: MOCK_IMAGE_ASSETS.workoutExerciseSquat,
            prescribedSets: [
              { index: 1, reps: 12, weightKg: 20, recoverySeconds: 60 },
              { index: 2, reps: 12, weightKg: 20, recoverySeconds: 60 },
              { index: 3, reps: 10, weightKg: 22, recoverySeconds: 75 },
            ],
            loggedSets: [
              { index: 1, reps: 12, weightKg: 20, recoverySeconds: 60 },
              { index: 2, reps: 12, weightKg: 20, recoverySeconds: 60 },
              { index: 3, reps: 10, weightKg: 22, recoverySeconds: 75 },
            ],
          },
          {
            id: "ex-2",
            title: "Romanian Deadlift",
            imageUrl: MOCK_IMAGE_ASSETS.workoutExerciseDeadlift,
            prescribedSets: [
              { index: 1, reps: 10, weightKg: 35, recoverySeconds: 75 },
              { index: 2, reps: 10, weightKg: 35, recoverySeconds: 75 },
              { index: 3, reps: 8, weightKg: 40, recoverySeconds: 90 },
            ],
            loggedSets: [
              { index: 1, reps: 10, weightKg: 35, recoverySeconds: 75 },
              { index: 2, reps: 10, weightKg: 35, recoverySeconds: 75 },
              { index: 3, reps: 8, weightKg: 40, recoverySeconds: 90 },
            ],
          },
        ],
      },
      {
        id: "group-2",
        typeLabel: "Cardio Finisher",
        description: "Short aerobic block to close the session.",
        totalMinutes: 8,
        exercises: [
          {
            id: "ex-3",
            title: "Air Bike",
            imageUrl: MOCK_IMAGE_ASSETS.workoutCard,
            prescribedSets: [
              { index: 1, workTimeMinutes: 8, workZone: 3, watts: 180 },
            ],
            loggedSets: [
              { index: 1, workTimeMinutes: 8, workZone: 3, watts: 188 },
            ],
          },
        ],
      },
    ],
    ui: {
      workoutTypeLabels: {
        scheduled: "Scheduled workout",
        logged: "Logged workout",
      },
      stateLabels: {
        automatically_logged: "Automatically logged",
        manually_logged: "Manually logged",
        scheduled: "Scheduled",
        in_progress: "In progress",
        completed: "Completed",
      },
      calories: {
        label: "Calories",
        targetPrefix: "Target",
        targetSuffix: "kcal",
      },
      sectionTitles: {
        overview: "Overview",
        wearableData: "Wearable Data",
        exerciseGroups: "Exercise Groups",
      },
      wearable: {
        hasHeartRateDataLabel: "HR data",
        noHeartRateDataLabel: "No HR data",
        caloriesUnitSuffix: "kcal",
        durationUnitSuffix: "min",
      },
      exerciseSkippedLabel: "Exercise skipped",
      setSections: {
        prescribed: "Prescribed",
        logged: "Logged",
      },
      groupMeta: {
        minutesSuffix: "min",
        roundsSuffix: "rounds",
      },
      setTable: {
        placeholderValue: "-",
        recoverySecondsSuffix: "s",
        columnLabels: {
          index: "#",
          reps: "Reps",
          weightKg: "Kg",
          recoverySeconds: "Rec",
          workTimeMinutes: "Min",
          workZone: "Zone",
          watts: "Watts",
          speed: "Speed",
          rpm: "RPM",
          stages: "Stages",
        },
      },
    },
  };
}
